import { ref } from 'vue'
import { getDefaultFieldOrder, getAllFieldKeys } from '@/config/ipTableFields'

const STORAGE_KEY = 'ipTable.fieldSettings'

// Shared state across all component instances
const fieldSettings = ref(loadFromStorage())

/**
 * Load settings from localStorage
 */
function loadFromStorage() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      return JSON.parse(stored)
    }
    return {
      order: null,
      disabled: []
    }
  } catch {
    return {
      order: null,
      disabled: []
    }
  }
}

/**
 * Save settings to localStorage
 */
function saveToStorage() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(fieldSettings.value))
  } catch {
    // Ignore storage errors
  }
}

export function useIpTableSettings() {
  /**
   * Check if a field is enabled
   * @param {string} field - The field key
   * @returns {boolean} True if the field is enabled
   */
  function isFieldEnabled(field) {
    const disabled = fieldSettings.value.disabled
    if (!disabled || !Array.isArray(disabled)) {
      return true
    }
    return !disabled.includes(field)
  }

  /**
   * Toggle a field's visibility
   * @param {string} field - The field key
   */
  function toggleField(field) {
    const disabled = fieldSettings.value.disabled || []
    const index = disabled.indexOf(field)

    if (index === -1) {
      // Field is enabled, disable it
      fieldSettings.value.disabled = [...disabled, field]
    } else {
      // Field is disabled, enable it
      fieldSettings.value.disabled = disabled.filter((_, i) => i !== index)
    }

    saveToStorage()
  }

  /**
   * Get the field order
   * Default order comes from config, or custom order if set
   * @returns {string[]} Ordered field keys
   */
  function getFieldOrder() {
    const customOrder = fieldSettings.value.order
    const allFields = getAllFieldKeys()

    if (customOrder && Array.isArray(customOrder) && customOrder.length > 0) {
      // User has customized the order
      // Ensure all fields are included (custom order may have missing/old fields)
      const allSet = new Set(allFields)
      const result = []

      // Add fields from custom order that exist
      for (const field of customOrder) {
        if (allSet.has(field)) {
          result.push(field)
          allSet.delete(field)
        }
      }

      // Add any new fields not in custom order
      result.push(...allSet)

      return result
    }

    // Use default order from config
    return getDefaultFieldOrder()
  }

  /**
   * Set custom field order
   * @param {string[]} orderedFields - Fields in desired order
   */
  function setFieldOrder(orderedFields) {
    fieldSettings.value.order = [...orderedFields]
    saveToStorage()
  }

  /**
   * Get enabled fields in the correct order
   * @returns {string[]} Ordered and enabled field keys
   */
  function getOrderedEnabledFields() {
    const orderedFields = getFieldOrder()
    return orderedFields.filter(field => isFieldEnabled(field))
  }

  /**
   * Reset all settings (order and visibility)
   */
  function resetSettings() {
    fieldSettings.value = {
      order: null,
      disabled: []
    }
    saveToStorage()
  }

  /**
   * Check if there are any custom settings
   * @returns {boolean} True if there are custom order or disabled fields
   */
  function hasCustomSettings() {
    const settings = fieldSettings.value
    const hasCustomOrder = settings.order && Array.isArray(settings.order) && settings.order.length > 0
    const hasDisabledFields = settings.disabled && Array.isArray(settings.disabled) && settings.disabled.length > 0
    return hasCustomOrder || hasDisabledFields
  }

  /**
   * Get the number of enabled fields
   * @returns {number} Count of enabled fields
   */
  function getEnabledCount() {
    return getOrderedEnabledFields().length
  }

  /**
   * Get the total number of fields
   * @returns {number} Total count of fields
   */
  function getTotalCount() {
    return getAllFieldKeys().length
  }

  return {
    isFieldEnabled,
    toggleField,
    getFieldOrder,
    setFieldOrder,
    getOrderedEnabledFields,
    resetSettings,
    hasCustomSettings,
    getEnabledCount,
    getTotalCount
  }
}
